import Button from '@/features/shared/Button';
import { useProcessFacets } from '@/features/shared/Hooks/facets/useProcessFacets';
import { MuiAppBar, MuiBox, MuiTab, MuiTabs, MuiTypography } from '@/features/shared/material-ui';
import { Theme } from '@/utils/constants';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import clsx from 'clsx';
import { orderBy } from 'lodash';
import React, { memo, PropsWithChildren, useCallback, useContext, useState } from 'react';
import useDeepCompareEffect from 'use-deep-compare-effect';
import ElasticComponentContext from '../ElasticComponentContext';
import FacetFilters from '../FacetFilters/FacetFilters';
import { TabFilterProps } from './TabFilter.interface';
import useStyles from './TabFilter.styles';

function a11yProps(index: any) {
  return {
    id: `scrollable-force-tab-${index}`,
    'aria-controls': `scrollable-force-tabpanel-${index}`,
  };
}

function TabPanel(props: any) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`scrollable-force-tabpanel-${index}`}
      aria-labelledby={`scrollable-force-tab-${index}`}
      {...other}
    >
      {value === index && <MuiBox>{children}</MuiBox>}
    </div>
  );
}

const TabFilter = ({
  theme = Theme.light,
  hasTitle = true,
  size,
  renderLabel,
  onChange = () => null,
  labels = {},
  isSorted = false,
  onClose = () => null,
}: PropsWithChildren<TabFilterProps>) => {
  const classes = useStyles({});
  const {
    facets: elasticFacets,
    forceUpdate = () => null,
    manualUpdate,
  } = useContext(ElasticComponentContext);
  const [activeTab, setActiveTab] = useState<null | number>(0);
  const [activeTabPanel, setActiveTabPanel] = useState<null | number>(-1);
  const [allFilters, setAllFilters] = useState<any[]>([]);
  const facets = useProcessFacets(elasticFacets);

  const handleClick = (nextValue: number) => {
    setActiveTabPanel(nextValue === activeTabPanel ? -1 : nextValue);
    setActiveTab(nextValue);
  };

  const buildFilterMenu = useCallback(() => {
    const filtersWithOptions = facets.filter((filter: any) => filter.options.length > 0);
    return filtersWithOptions.map((filter: any) => ({
      ...filter,
      options: isSorted
        ? orderBy(filter.options, [({ value }) => value?.toLowerCase()], ['asc'])
        : filter.options,
    }));
  }, [facets, isSorted]);

  const handleApplyFilters = () => {
    forceUpdate();
    setActiveTabPanel(-1);
    onClose();
  };

  useDeepCompareEffect(() => {
    const sortedFilters = facets.length > 0 ? buildFilterMenu() : [];
    setAllFilters(sortedFilters);
  }, [facets, isSorted]);

  const rootClassName = clsx(
    classes.root,
    `${theme}-theme`,
    size === 'small' && 'typography-small',
  );

  return (
    <div className={rootClassName}>
      <MuiAppBar position="static" color="default" className={classes.filters} elevation={0}>
        {hasTitle && (
          <MuiTypography variant="overline" className="title" component="div">
            {labels?.filters || 'Filters'}
          </MuiTypography>
        )}
        <MuiTabs
          className={classes.tabs}
          value={activeTab}
          variant="scrollable"
          scrollButtons="on"
          indicatorColor="primary"
          textColor="inherit"
          aria-label="scrollable force tabs example"
        >
          {allFilters.map((item, idx: number) => (
            <MuiTab
              className={classes.tab}
              onClick={() => handleClick(idx)}
              label={
                <span>
                  {labels[item.name] || item.label}
                  <ExpandMoreIcon className={classes.tabIcon} fontSize="small" />
                </span>
              }
              key={item.name}
              {...a11yProps(idx)}
            />
          ))}
        </MuiTabs>
      </MuiAppBar>
      {allFilters.map((item, idx: number) => (
        <TabPanel className={classes.tabPanel} value={activeTabPanel} index={idx} key={item.name}>
          <MuiTypography variant="h6" className={classes.tabPanelTitle}>
            {labels[item.name] || item.label}
          </MuiTypography>
          <FacetFilters
            facet={item.name}
            options={item.options}
            onChange={onChange}
            renderLabel={renderLabel}
            labels={labels}
          />
          {manualUpdate && (
            <div className={classes.tabPanelButton}>
              <Button theme={Theme.light} outlined onClick={handleApplyFilters}>
                <span>{labels?.close_apply_filters || 'Close & Apply Filters'}</span>
              </Button>
            </div>
          )}
        </TabPanel>
      ))}
    </div>
  );
};

export default memo(TabFilter);
